import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DonkeyMascot } from "@/components/DonkeyMascot";
import { PublicAmbient, KineticHeading } from "@/components/public/PublicVisuals";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, CheckCircle2, AlertCircle, Lightbulb, Search } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

// Live profile categories — the interrogation visibly builds an intelligence model as answers accumulate.
const CATS = [
  { k: "Capability", keys: ["selling_experience", "delivery_experience", "preference"] },
  { k: "Resources", keys: ["weekly_time", "budget"] },
  { k: "Market reach", keys: ["customer_access"] },
  { k: "Drive & goals", keys: ["desired_income", "outcome", "comfort_conversations", "predictable_income_need"] },
];

const QUESTIONS = [
  { key: "weekly_time", q: "How much spare time do you have each week?",
    options: [["none", "Almost none"], ["few", "A few hours"], ["some", "Several hours"], ["lots", "A lot"]] },
  { key: "budget", q: "What is the maximum you could safely risk to start?",
    options: [["zero", "Effectively nothing"], ["small", "Up to a few hundred £"], ["medium", "Up to ~£1,000"], ["large", "More than £1,000"]] },
  { key: "desired_income", q: "What are you hoping this brings in?",
    options: [["topup", "A modest top-up"], ["replace", "Replace part of my income"], ["ambitious", "A serious business"]] },
  { key: "outcome", q: "What matters most to you?",
    options: [["income", "Reliable income"], ["freedom", "Flexibility & freedom"], ["passion", "Doing something I enjoy"], ["growth", "Building something big"]] },
  { key: "selling_experience", q: "Have you sold anything before?",
    options: [["none", "Never"], ["little", "A little"], ["some", "Some"], ["lots", "Plenty"]] },
  { key: "delivery_experience", q: "Have you delivered paid work or a service before?",
    options: [["none", "Never"], ["little", "A little"], ["some", "Some"], ["lots", "Plenty"]] },
  { key: "customer_access", q: "How easily can you reach potential customers?",
    options: [["none", "No idea where they are"], ["few", "A few contacts"], ["some", "A reasonable network"], ["strong", "Strong access"]] },
  { key: "preference", q: "What kind of work appeals most?",
    options: [["service", "Services"], ["product", "Physical products"], ["digital", "Digital / online"], ["unsure", "Not sure yet"]] },
  { key: "comfort_conversations", q: "How comfortable are you talking to customers?",
    options: [["low", "Uncomfortable"], ["medium", "It's fine"], ["high", "Very comfortable"]] },
  { key: "predictable_income_need", q: "How much do you need predictable income right now?",
    options: [["low", "Not much"], ["medium", "Somewhat"], ["high", "A great deal"]] },
];

export default function DonkeyCheck() {
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const q = QUESTIONS[step];
  const progress = (step / QUESTIONS.length) * 100;

  const choose = (val) => {
    const next = { ...answers, [q.key]: val };
    setAnswers(next);
    if (step + 1 < QUESTIONS.length) setStep(step + 1);
    else submit(next);
  };

  const submit = async (payload) => {
    setBusy(true);
    try {
      const { data } = await api.post("/preview/donkey-check", payload);
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not run the check.");
    } finally {
      setBusy(false);
    }
  };

  // Staged result reveal — each block arrives in sequence so the verdict feels assembled, not dumped.
  const block = (delay) => ({
    initial: reduce ? false : { opacity: 0, y: 24 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5, delay, ease: EASE },
  });

  if (result) {
    return (
      <div className="relative overflow-hidden min-h-screen">
        <PublicAmbient intensity={0.45} />
        <div className="relative z-10 mx-auto max-w-2xl px-5 sm:px-8 py-14">
          <motion.div
            className="flex items-center gap-4 mb-6"
            initial={reduce ? false : { opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
          >
            <DonkeyMascot size={64} className="opacity-95 donkey-alive drop-shadow-[0_0_30px_rgba(249,115,22,0.22)]" />
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-donkey-orange mb-1">Your Donkey Check</p>
              <KineticHeading as="h1" text={result.result} className="text-2xl sm:text-3xl" />
              <span className="sr-only" data-testid="donkey-check-result">{result.result}</span>
            </div>
          </motion.div>

          <motion.div className="card-donkey rounded-2xl p-6 mb-5" {...block(0.12)}>
            <h3 className="font-heading font-semibold text-donkey-warm flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-5 h-5 text-donkey-green" /> Likely strengths
            </h3>
            <ul className="space-y-2 text-sm text-donkey-warm/85">
              {result.strengths.map((s, i) => <li key={i} className="flex gap-2"><span className="text-donkey-green">•</span>{s}</li>)}
            </ul>
          </motion.div>

          <motion.div className="card-donkey rounded-2xl p-6 mb-5" {...block(0.24)}>
            <h3 className="font-heading font-semibold text-donkey-warm flex items-center gap-2 mb-3">
              <AlertCircle className="w-5 h-5 text-donkey-amber" /> Important constraints
            </h3>
            <ul className="space-y-2 text-sm text-donkey-warm/85">
              {result.constraints.map((s, i) => <li key={i} className="flex gap-2"><span className="text-donkey-amber">•</span>{s}</li>)}
            </ul>
          </motion.div>

          <motion.div className="card-donkey rounded-2xl p-6 mb-5" {...block(0.36)}>
            <h3 className="font-heading font-semibold text-donkey-warm flex items-center gap-2 mb-3">
              <Lightbulb className="w-5 h-5 text-donkey-orange" /> One sensible next step
            </h3>
            <p className="text-sm text-donkey-warm/85">{result.preparation_step}</p>
          </motion.div>

          <motion.div className="rounded-2xl border border-donkey-orange/30 bg-donkey-deep p-6 mb-6" {...block(0.48)}>
            <h3 className="font-heading font-semibold text-donkey-warm flex items-center gap-2 mb-3">
              <Search className="w-5 h-5 text-donkey-orange" /> What the paid interview would investigate
            </h3>
            <ul className="space-y-2 text-sm text-donkey-grey">
              {result.paid_interview_would_investigate.map((s, i) => <li key={i} className="flex gap-2"><span className="text-donkey-orange">•</span>{s}</li>)}
            </ul>
          </motion.div>

          <motion.p className="text-xs text-donkey-amber/90 mb-6" data-testid="donkey-check-disclaimer" {...block(0.56)}>{result.disclaimer}</motion.p>

          <motion.div className="flex flex-wrap gap-3" {...block(0.64)}>
            <Button onClick={() => navigate("/pricing")} className="bg-donkey-orange text-donkey-black font-semibold h-11 px-6 transition-transform hover:-translate-y-0.5" data-testid="check-to-pricing">
              Start with Prove — £29 <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button variant="outline" onClick={() => { setResult(null); setStep(0); setAnswers({}); }} className="border-white/15 bg-transparent text-donkey-warm hover:bg-white/5 h-11">
              Run it again
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden min-h-screen">
      <PublicAmbient intensity={0.4} />
      <div className="relative z-10 mx-auto max-w-4xl px-5 sm:px-8 py-14">
        <button onClick={() => (step > 0 ? setStep(step - 1) : navigate("/meet-the-donkey"))} className="text-sm text-donkey-grey hover:text-donkey-warm flex items-center gap-1 mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="grid lg:grid-cols-[1fr_270px] gap-8 items-start">
          <div className="min-w-0">
            <div className="h-1 w-full bg-white/8 rounded-full overflow-hidden mb-8">
              <motion.div
                className="h-full bg-donkey-orange"
                initial={false}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5, ease: EASE }}
                style={{ boxShadow: "0 0 12px rgba(249,115,22,0.5)" }}
              />
            </div>
            <p className="text-xs uppercase tracking-[0.2em] text-donkey-orange mb-3">Free Donkey Check · {step + 1} of {QUESTIONS.length}</p>
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={reduce ? false : { opacity: 0, x: 28 }}
                animate={{ opacity: 1, x: 0 }}
                exit={reduce ? undefined : { opacity: 0, x: -28 }}
                transition={{ duration: 0.38, ease: EASE }}
              >
                <h1 className="font-heading font-bold text-2xl sm:text-3xl text-donkey-warm mb-8">{q.q}</h1>
                <div className="grid gap-3">
                  {q.options.map(([val, label], i) => (
                    <motion.button
                      key={val}
                      onClick={() => choose(val)}
                      disabled={busy}
                      initial={reduce ? false : { opacity: 0, y: 14 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.32, delay: reduce ? 0 : 0.06 + i * 0.05, ease: EASE }}
                      whileHover={reduce ? undefined : { x: 4 }}
                      className="text-left rounded-xl border border-white/12 bg-donkey-deep px-5 py-4 text-donkey-warm/90 hover:border-donkey-orange/50 hover:bg-white/5 transition-colors disabled:opacity-60"
                      data-testid={`check-option-${q.key}-${val}`}
                    >
                      {label}
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
            <p className="text-xs text-donkey-grey mt-8">
              This free check considers only your initial answers. It does not generate business opportunities or research
              customer demand.
            </p>
          </div>

          {/* LIVE intelligence profile — assembles from what you answer */}
          <div className="lg:sticky lg:top-6 card-donkey rounded-2xl p-5" data-testid="check-profile">
            <div className="flex items-center gap-3 mb-4">
              <img src="/mascot/thinking.png" alt="" className="h-12 w-auto object-contain" />
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-[0.15em] text-donkey-orange">Building your profile</p>
                <p className="text-xs text-donkey-grey">{Object.keys(answers).length} of {QUESTIONS.length} signals captured</p>
              </div>
            </div>
            <div className="space-y-3">
              {CATS.map((c) => {
                const a = c.keys.filter((k) => answers[k] != null).length;
                const pct = Math.round((a / c.keys.length) * 100);
                return (
                  <div key={c.k}>
                    <div className="flex items-center justify-between text-[11px] mb-1">
                      <span className="text-donkey-warm/80">{c.k}</span>
                      <span className="text-donkey-orange">{a ? `${pct}%` : "—"}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/8 overflow-hidden">
                      <motion.div className="h-full bg-gradient-to-r from-donkey-orange to-donkey-amber" initial={false} animate={{ width: `${pct}%` }} transition={{ type: "spring", stiffness: 140, damping: 20 }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-5 pt-4 border-t border-white/10">
              <p className="text-[11px] uppercase tracking-wider text-donkey-grey mb-1.5">Readiness confidence</p>
              <div className="h-2 rounded-full bg-white/8 overflow-hidden">
                <motion.div className="h-full bg-gradient-to-r from-donkey-orange via-donkey-amber to-donkey-warmorange" initial={false} animate={{ width: `${(Object.keys(answers).length / QUESTIONS.length) * 100}%` }} transition={{ type: "spring", stiffness: 120, damping: 20 }} style={{ boxShadow: "0 0 10px rgba(249,115,22,0.5)" }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
